import React, { createContext, useContext, useReducer } from "react";

export interface SealRegistrationFormData {
  fullName: string;
  jobTitle: string;
  email: string;
  confirmEmail: string;
  legalOrganizationName: string;
  registeredName: string;
  organizationWebsite: string;
  organizationCountry: string;
  roles: Record<string, boolean>;
  intendedUse: Record<string, boolean>;
  packagingConfirmation: boolean;
  vineyardUrls: Array<{ value: string }>;
  wineNotes: string;
  attestationsChecked: boolean[];
  agreeToTerms: boolean;
  communicationsRequired: boolean;
  communicationsOptional: boolean;
  conferenceInterest: boolean;
  donationOption: "none" | "50" | "100" | "250" | "other";
  donationOtherAmount: string;
}

export interface SealRegistrationState {
  formData: SealRegistrationFormData;
}

const initialFormData: SealRegistrationFormData = {
  fullName: "",
  jobTitle: "",
  email: "",
  confirmEmail: "",
  legalOrganizationName: "",
  registeredName: "",
  organizationWebsite: "",
  organizationCountry: "",
  roles: {
    vineyardOwner: false,
    producer: false,
    brandOwner: false,
    importer: false,
    retailer: false,
  },
  intendedUse: {
    packaging: false,
    website: false,
    marketing: false,
    shelfTalkers: false,
    wineLists: false,
    signage: false,
    otherPromo: false,
  },
  packagingConfirmation: false,
  vineyardUrls: [{ value: "" }],
  wineNotes: "",
  attestationsChecked: Array(11).fill(false),
  agreeToTerms: false,
  communicationsRequired: false,
  communicationsOptional: false,
  conferenceInterest: false,
  donationOption: "none",
  donationOtherAmount: "",
};

const initialState: SealRegistrationState = {
  formData: initialFormData,
};

type SealRegistrationAction =
  | { type: "SET_FORM_DATA"; payload: SealRegistrationFormData }
  | { type: "RESET" };

function sealRegistrationReducer(
  state: SealRegistrationState,
  action: SealRegistrationAction
): SealRegistrationState {
  switch (action.type) {
    case "SET_FORM_DATA":
      return { ...state, formData: action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

interface SealRegistrationContextValue {
  state: SealRegistrationState;
  dispatch: React.Dispatch<SealRegistrationAction>;
}

const SealRegistrationContext = createContext<SealRegistrationContextValue | null>(null);

export const SealRegistrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(sealRegistrationReducer, initialState);
  return (
    <SealRegistrationContext.Provider value={{ state, dispatch }}>
      {children}
    </SealRegistrationContext.Provider>
  );
};

export const useSealRegistration = (): SealRegistrationContextValue => {
  const context = useContext(SealRegistrationContext);
  if (!context) {
    throw new Error("useSealRegistration must be used within a SealRegistrationProvider");
  }
  return context;
};

export const getDonationAmount = (formData: SealRegistrationFormData): number => {
  switch (formData.donationOption) {
    case "50": return 50;
    case "100": return 100;
    case "250": return 250;
    case "other": {
      const parsed = parseFloat(formData.donationOtherAmount);
      return isNaN(parsed) || parsed < 0 ? 0 : Math.floor(parsed);
    }
    default: return 0;
  }
};

export const getSelectedRoles = (roles: Record<string, boolean>): string[] => {
  const roleLabels: Record<string, string> = {
    vineyardOwner: "Vineyard owner/operator",
    producer: "Wine producer",
    brandOwner: "Wine brand owner",
    importer: "Importer",
    retailer: "Retailer",
  };
  return Object.entries(roles)
    .filter(([, checked]) => checked)
    .map(([key]) => roleLabels[key] || key);
};

export const getSelectedIntendedUses = (intendedUse: Record<string, boolean>): string[] => {
  const useLabels: Record<string, string> = {
    packaging: "Packaging",
    website: "Website/e-commerce",
    marketing: "Marketing materials",
    shelfTalkers: "Shelf talkers/displays",
    wineLists: "Wine lists",
    signage: "Signage",
    otherPromo: "Other promotional",
  };
  return Object.entries(intendedUse)
    .filter(([, checked]) => checked)
    .map(([key]) => useLabels[key] || key);
};

export const getRegistrationCategory = (roles: Record<string, boolean>): string => {
  const selected = Object.entries(roles).filter(([, v]) => v).map(([k]) => k);
  if (selected.includes("brandOwner") || selected.includes("producer")) {
    return "Wine brand";
  }
  if (selected.includes("vineyardOwner")) return "Vineyard business";
  if (selected.includes("importer")) return "Importing business";
  if (selected.includes("retailer")) return "Retail business";
  return "Organization";
};
